import { FC, useContext, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import useI18n from "../../hooks/useTranslations";
import { useConfigurationsApi } from "../../http/useConfigurations";
import { useProjectsApi } from "../../http/useProjects";
import SvgAdd from "../icons/Add";
import CreateProjectForm from "../projects/CreateProjectForm";
import Button from "../ui/Button";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Input from "../ui/Input";
import ModalDialog from "../ui/ModalDialog";
import { CustomSelect } from "../ui/select/CustomSelect";
import styles from "./DuplicateConfigurationDialog.module.css";

interface DuplicateConfigurationDialogForm {
  project: {
    id: string;
    name: string;
  } | null;
  name: string;
}

interface DuplicateConfigurationDialogProps {
  configuration: {
    id: string;
    name: string;
    project?: {
      id: string;
      name: string;
    };
  };
  onClose: () => void;
  onSave: () => void;
  isAdmin?: boolean;
}

const DuplicateConfigurationDialog: FC<DuplicateConfigurationDialogProps> = ({
  configuration,
  onClose,
  onSave,
  isAdmin,
}: DuplicateConfigurationDialogProps) => {
  const i18n = useI18n();
  const projectsApi = useProjectsApi();
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());
  const configurationsApi = useConfigurationsApi();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [page, setPage] = useState<"configuration" | "project">("configuration");
  const [newProjectId, setNewProjectId] = useState<string>();
  const [savedFormValues, setSavedFormValues] = useState<Partial<DuplicateConfigurationDialogForm>>();

  const { data, trigger } = projectsApi.getMyProjects(aborter.current.signal);

  useEffect(() => {
    trigger();
  }, []);

  const editableProjects = data?.filter((x) => x.canEdit);
  const defaultValues: Partial<DuplicateConfigurationDialogForm> = {
    project: editableProjects?.find((x) => x.id === configuration.project?.id) ?? null,
    name: configuration.name + ` (${i18n.translation.common.copy})`,
  };

  return (
    <ModalDialog
      onClose={() => {
        if (page == "configuration") {
          onClose();
        } else {
          setPage("configuration");
        }
      }}
      title={page == "configuration" ? i18n.translation.configurations.duplicate : i18n.translation.projects.createNew}
    >
      {page == "configuration" && (
        <Form<DuplicateConfigurationDialogForm>
          defaultValues={savedFormValues ?? defaultValues}
          disabled={isSubmitting}
          onSubmit={() => async (formData) => {
            setIsSubmitting(true);
            return configurationsApi
              .duplicateConfiguration(
                configuration.project?.id,
                configuration.id,
                {
                  projectId: formData.project?.id,
                  name: formData.name,
                },
                isAdmin,
              )
              .then(() => onSave())
              .catch((err) => dispatch(showErrorNotification(i18n, err)))
              .finally(() => setIsSubmitting(false));
          }}
          submitText={i18n.translation.common.ok}
          secondaryButton={
            <Button buttonProps={{ disabled: isSubmitting, onClick: () => onClose() }} type="secondary">
              {i18n.translation.common.cancel}
            </Button>
          }
        >
          {({ register, formState: { errors }, control, reset, setValue, getValues }) => {
            useEffect(() => {
              reset(defaultValues);
            }, [configuration.id]);

            useEffect(() => {
              if (newProjectId) {
                trigger().then((items) => {
                  setValue("project", items.find((x) => x.id === newProjectId) ?? null);
                });
              }
            }, [newProjectId]);

            return (
              <FormFieldsContainer fullWidth>
                <FormField label={i18n.translation.common.newConfigurationName} error={errors.name}>
                  {({ labelId, isOptional, isInvalid }) => (
                    <Input
                      id={labelId}
                      isInvalid={isInvalid}
                      disabled={isSubmitting}
                      {...register("name", {
                        required: !isOptional,
                      })}
                    />
                  )}
                </FormField>
                <Controller
                  name="project"
                  control={control}
                  render={({ field, fieldState }) => (
                    <FormField
                      label={i18n.translation.common.selectProjectInwhichCreateCopy}
                      error={fieldState.error}
                      isOptional
                    >
                      {({ labelId }) => (
                        <div className={styles.selectWithButton}>
                          <CustomSelect
                            {...field}
                            inputId={labelId}
                            options={editableProjects}
                            isDisabled={isSubmitting}
                            isClearable
                            getOptionValue={(x) => x.id}
                            getOptionLabel={(x) => x.name}
                            onChange={(selectedOption) => setValue("project", selectedOption ?? null)}
                          />
                          <Button
                            buttonProps={{
                              title: i18n.translation.projects.createNew,
                              className: styles.button,
                              disabled: isSubmitting,
                              onClick: () => {
                                setSavedFormValues(getValues());
                                setPage("project");
                              },
                            }}
                            glyph={SvgAdd}
                          />
                        </div>
                      )}
                    </FormField>
                  )}
                />
              </FormFieldsContainer>
            );
          }}
        </Form>
      )}
      {page == "project" && (
        <CreateProjectForm
          onSubmit={(projectId: string) => {
            setIsSubmitting(false);
            setNewProjectId(projectId);
            setPage("configuration");
          }}
          isSubmitting={isSubmitting}
          setIsSubmitting={setIsSubmitting}
          closeModal={() => setPage("configuration")}
        />
      )}
    </ModalDialog>
  );
};

export default DuplicateConfigurationDialog;
